import React, { useEffect } from 'react';
import {
  cssColorValue,
  LogoSpotify,
  PopoverNavigation,
  PopoverNavigationItem,
  semanticColors,
  spacer12,
  spacer16,
  spacer24,
  spacer32,
  spacer8,
  TextLink,
} from '@spotify-internal/encore-web/core';
import styled from 'styled-components';

import Ellipsis from 'components/Ellipsis';
import { MastheadHeaderData } from 'lib/sanity/types';
import { useTranslation } from 'providers/translations/TranslationProvider';
import { isMastheadLink, isMastheadLinkList } from 'utils/type-guards';
import { isLinkDisabled, isLinkVisible } from 'components/Masthead/shared/utils';
import { NavBarPopoverList, NavBarPopoverListTitle } from 'components/Masthead/Header/Popover/Shared';
import { NavBarPopoverListItem } from 'components/Masthead/Header/Popover/LinkItem';
import { useUbiMastheadHeader } from 'utils/ubi/pages/useUbiHeader';

const StyledPopoverNavigation = styled(PopoverNavigation)`
  background: ${cssColorValue(semanticColors.backgroundBase)};
  bottom: 0;
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: 60px ${spacer16} ${spacer16};
  position: fixed;
  inset-inline-end: 0;
  top: 0;
  min-width: 320px;
  max-width: 100vw;

  &&:focus {
    outline: none;
  }
`;

const StyledEllipsis = styled(Ellipsis)`
  margin-bottom: ${spacer16};
  margin-left: ${spacer8};
`;

const NavList = styled.ul`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: ${spacer16};
  list-style-type: none;
  margin-bottom: ${spacer32};
  margin-top: 0;
  padding: 0;
`;

const StyledPopoverNavigationItem = styled(PopoverNavigationItem)`
  background-color: ${cssColorValue(semanticColors.backgroundTintedBase)};
  border-radius: ${spacer8};
  padding: ${spacer8} ${spacer12};
`;

const LogoWrapper = styled(TextLink)`
  && {
    text-decoration: none;
    width: fit-content;

    &:focus {
      box-shadow: none;
      transition: none;
      outline: 5px auto -webkit-focus-ring-color;
    }
  }
`;

const StyledLogo = styled(LogoSpotify)`
  color: ${cssColorValue(semanticColors.textBase)};
  fill: ${cssColorValue(semanticColors.textBase)};
  height: ${spacer24};
  width: 78px;
`;

type Props = {
  headerEntry: MastheadHeaderData;
  closeBackdrop: () => void;
  dir: string;
  username?: string;
};

export const ProfileSideBar = ({ headerEntry, closeBackdrop, dir, username }: Props) => {
  const { t } = useTranslation();
  const ubi = useUbiMastheadHeader();

  const onProfileLinkClick = (identifier: string, uri: string) => {
    ubi.logProfileLinkInteraction({ container: 'sidemenu', identifier, uri });
  };

  const isLoggedIn = username !== undefined;

  useEffect(() => {
    ubi.logProfileLinksContainerImpression('sidemenu');
  }, [ubi]);

  return (
    <StyledPopoverNavigation
      as="nav"
      dir={dir}
      role="dialog"
      tabIndex={0}
      aria-label={t('navbar_side_menu_popover_label')}
    >
      <StyledEllipsis variant="titleLarge">{username ? `Hi ${username}` : `Hello`}!</StyledEllipsis>
      <NavList>
        {headerEntry.profileLinks?.filter(isMastheadLinkList).map(item => (
          <StyledPopoverNavigationItem key={item.label}>
            <NavBarPopoverListTitle>{item.label}</NavBarPopoverListTitle>
            <NavBarPopoverList>
              {item.entries
                .filter(({ link }) => isLinkVisible(link, isLoggedIn))
                .map(({ link, nestingLevel }, i) => (
                  <NavBarPopoverListItem
                    key={i}
                    {...link}
                    nestingLevel={nestingLevel}
                    isDisabled={isLinkDisabled(link, isLoggedIn)}
                    onClick={() => {
                      onProfileLinkClick(link.trackingID, link.href);
                      closeBackdrop();
                    }}
                  />
                ))}
            </NavBarPopoverList>
          </StyledPopoverNavigationItem>
        ))}
        <StyledPopoverNavigationItem>
          {headerEntry.secondaryLinks
            ?.filter(isMastheadLink)
            ?.filter(item => isLinkVisible(item, isLoggedIn))
            .map((item, i) => (
              <NavBarPopoverListItem
                key={i}
                {...item}
                nestingLevel={i}
                isDisabled={isLinkDisabled(item, isLoggedIn)}
                onClick={closeBackdrop}
              />
            ))}
          {headerEntry.profileLinks
            ?.filter(isMastheadLink)
            .filter(item => isLinkVisible(item, isLoggedIn))
            .map((item, i) => (
              <NavBarPopoverListItem
                key={i}
                {...item}
                nestingLevel={i}
                isDisabled={isLinkDisabled(item, isLoggedIn)}
                onClick={() => {
                  onProfileLinkClick(item.trackingID, item.href);
                  closeBackdrop();
                }}
              />
            ))}
        </StyledPopoverNavigationItem>
      </NavList>
      <LogoWrapper href="https://www.spotify.com" data-testid="navbar_side_menu_logo">
        <StyledLogo />
      </LogoWrapper>
    </StyledPopoverNavigation>
  );
};
